// =============================================================================
// Trial Expired Modal - Blocking modal shown once when trial first expires
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlanGate } from '@/hooks/usePlanGate';
import { AlertTriangle } from 'lucide-react';

const DISMISSED_KEY = 'transakt-trial-expired-dismissed';

export function TrialExpiredModal() {
  const navigate = useNavigate();
  const { trialExpired } = usePlanGate();
  const [dismissed, setDismissed] = useState(false);

  // Check if already dismissed
  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY) === 'true') {
      setDismissed(true);
    }
  }, []);

  if (!trialExpired || dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const handleUpgrade = () => {
    handleDismiss();
    navigate('/settings?billing=upgrade');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-orange-500" />
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Your 14-day trial has ended
        </h2>

        <p className="text-gray-600 mb-6 text-sm">
          Upgrade to Pro to continue creating properties, uploading documents, and sending via DocuSign.
          Your existing data is safe and accessible.
        </p>

        <div className="space-y-3">
          <button
            onClick={handleUpgrade}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#17DDAB] to-[#009FE4] text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
          >
            Upgrade to Pro
          </button>

          <button
            onClick={handleDismiss}
            className="w-full px-6 py-3 text-gray-500 text-sm hover:text-gray-700 transition-colors"
          >
            Continue with Free Plan
          </button>
        </div>
      </div>
    </div>
  );
}
